import { centsToFormattedDollars } from '@fruitful/fruitils';
import React, { useState } from 'react';
import {
    ArrowStraight,
    BodyMediumEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    ChevronDownIcon,
    ChevronRightIcon,
    Icon,
    LabelRegular,
    MonoLabelRegular,
    View,
    XStack,
    YStack,
} from 'smoothie';

import type { IncomeSplitExecutionTransaction } from '../../../entityTypes';
import type {
    IncomeSplitPlanResponse,
    RulePlan,
    RuleSetPlan,
    RuleValidationDetail,
    SimulateIncomeSplitResponse,
} from '../../hooks/useSimulateIncomeSplit';
import { LineageAuditPanel } from './LineageAudit';

interface IncomeSplitSimulationResultsProps {
    result: IncomeSplitPlanResponse;
    fullResponse?: SimulateIncomeSplitResponse;
}

const RuleDataTag = ({ text, color }: { text: string; color: string }) => (
    <View
        alignSelf="flex-start"
        backgroundColor="$gray100"
        borderRadius={16}
        paddingVertical={4}
        paddingHorizontal={6}
    >
        <MonoLabelRegular color={color}>{text}</MonoLabelRegular>
    </View>
);

const RuleDataRow = ({ children }: { children: React.ReactNode }) => (
    <XStack gap={8} alignItems="center" flexWrap="wrap">
        <>{children}</>
    </XStack>
);

const formatRulePercentage = (percentage: string | number | undefined) => {
    if (percentage == null) {
        return 'N/A';
    }
    const numericPercentage = Number(percentage);
    if (!Number.isFinite(numericPercentage)) {
        return 'N/A';
    }
    return `${Math.floor(numericPercentage * 100)}%`;
};

const getNoTransferReason = (rule: RulePlan) => {
    const { limitDetails } = rule;
    if (limitDetails == null) {
        return null;
    }

    const reasons: string[] = [];
    if (limitDetails.monthlyMet) {
        reasons.push('Monthly limit reached');
    }
    if (limitDetails.yearlyMet) {
        reasons.push('Yearly limit reached');
    }
    if (limitDetails.hasReachedCapacity) {
        reasons.push('Capacity reached');
    }

    return reasons.length > 0 ? reasons.join(', ') : null;
};

const getDestinationAccount = (rule: RulePlan) => {
    if (rule.accountType === 'INTERNAL_INVESTMENT') {
        return (
            rule.destinationInternalInvestmentAccountDetails ??
            rule.destinationAccountDetails
        );
    }
    if (
        rule.accountType === 'INTERNAL_DEPOSITORY' ||
        rule.accountType === 'EXTERNAL'
    ) {
        return (
            rule.destinationDepositoryAccountDetails ??
            rule.destinationAccountDetails
        );
    }
    return rule.destinationAccountDetails;
};

const AccountTransferRoute = ({
    rule,
    sourceAccount,
}: {
    rule: RulePlan;
    sourceAccount?: IncomeSplitPlanResponse['sourceAccount'];
}) => {
    const destinationAccount = getDestinationAccount(rule);

    return (
        <YStack padding={8} borderRadius={8} backgroundColor="$gray100" gap={4}>
            {sourceAccount != null && (
                <XStack justifyContent="space-between" gap={8}>
                    <BodySmallRegular color="$gray1000">From</BodySmallRegular>
                    <YStack alignItems="flex-end" flex={1} minWidth={0}>
                        <BodySmallRegular color="$gray1200">
                            {sourceAccount.nickname}
                        </BodySmallRegular>
                        <MonoLabelRegular color="$gray1000">
                            {sourceAccount.accountNumberMasked}
                        </MonoLabelRegular>
                    </YStack>
                </XStack>
            )}
            {destinationAccount != null && (
                <XStack justifyContent="space-between" gap={8}>
                    <BodySmallRegular color="$gray1000">
                        Destination
                    </BodySmallRegular>
                    <YStack alignItems="flex-end" flex={1} minWidth={0}>
                        <BodySmallRegular color="$gray1200">
                            {destinationAccount.nickname}
                        </BodySmallRegular>
                        <MonoLabelRegular color="$gray1000">
                            {destinationAccount.accountNumberMasked}
                        </MonoLabelRegular>
                    </YStack>
                </XStack>
            )}
        </YStack>
    );
};

const YtdTransactionRows = ({
    transactions,
}: {
    transactions: IncomeSplitExecutionTransaction[];
}) => {
    const [isOpen, setIsOpen] = useState(false);

    const nonZeroTransactions = transactions.filter(
        (transaction) => transaction.amount !== 0,
    );
    if (nonZeroTransactions.length === 0) {
        return null;
    }

    return (
        <YStack gap={6}>
            <XStack
                onPress={() => {
                    setIsOpen((current) => !current);
                }}
                cursor="pointer"
                alignItems="center"
                justifyContent="space-between"
                gap={8}
            >
                <BodySmallRegular color="$gray1000" fontWeight="emphasized">
                    YTD transfers ({nonZeroTransactions.length})
                </BodySmallRegular>
                <Icon
                    icon={isOpen ? ChevronDownIcon : ChevronRightIcon}
                    size={16}
                    color="$gray1000"
                />
            </XStack>
            {isOpen &&
                nonZeroTransactions.map((transaction) => (
                    <XStack
                        key={transaction.id}
                        padding={8}
                        borderRadius={8}
                        backgroundColor="$gray100"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        gap={8}
                    >
                        <YStack gap={2} flex={1} minWidth={0}>
                            <MonoLabelRegular color="$gray1000">
                                {new Date(
                                    transaction.dateAdded,
                                ).toLocaleDateString()}
                            </MonoLabelRegular>
                            <BodySmallRegular color="$gray1000">
                                {transaction.destinationAccountDisplayName}
                            </BodySmallRegular>
                        </YStack>
                        <YStack gap={2} alignItems="flex-end">
                            <MonoLabelRegular color="$gray1200">
                                {centsToFormattedDollars(
                                    transaction.amount,
                                    false,
                                )}
                            </MonoLabelRegular>
                            <MonoLabelRegular
                                color={
                                    transaction.status === 'SUCCESS'
                                        ? '$green900'
                                        : '$gray1000'
                                }
                            >
                                {transaction.status}
                            </MonoLabelRegular>
                        </YStack>
                    </XStack>
                ))}
        </YStack>
    );
};

const RuleLimitUntilRow = ({
    label,
    cents,
}: {
    label: string;
    cents?: number | null;
}) => {
    if ((cents ?? 0) <= 0) {
        return null;
    }

    return (
        <RuleDataRow>
            <RuleDataTag text="UNTIL" color="$gray1200" />
            <MonoLabelRegular color="$gray1200">{label}</MonoLabelRegular>
            <RuleDataTag
                text={centsToFormattedDollars(cents ?? 0, false)}
                color="$green900"
            />
        </RuleDataRow>
    );
};

const RuleConfigSection = ({ rule }: { rule: RulePlan }) => {
    const { originalRule } = rule;
    const percentageValue = originalRule?.percentage ?? rule.percentage;

    return (
        <YStack padding={12} borderRadius={8} backgroundColor="$white" gap={8}>
            <BodySmallRegular color="$gray1000" fontWeight="emphasized">
                Rule Configuration
            </BodySmallRegular>
            <RuleDataRow>
                <RuleDataTag text="CONTRIBUTE" color="$gray1200" />
                <RuleDataTag
                    text={formatRulePercentage(percentageValue)}
                    color="$green900"
                />
            </RuleDataRow>
            <RuleLimitUntilRow
                label="available balance ="
                cents={originalRule?.targetBalanceCents}
            />
            <RuleLimitUntilRow
                label="monthly contribution ="
                cents={originalRule?.monthlyContributionMaxCents}
            />
            <RuleLimitUntilRow
                label="annual contribution ="
                cents={originalRule?.yearlyContributionMaxCents}
            />
        </YStack>
    );
};

const SimulationResultSection = ({
    rule,
    sourceAccount,
    ytdTransactions,
}: {
    rule: RulePlan;
    sourceAccount?: IncomeSplitPlanResponse['sourceAccount'];
    ytdTransactions: IncomeSplitExecutionTransaction[];
}) => {
    const transferAmount = rule.transferInfo.amountToTransfer;
    const transferType = rule.transferInfo.type;
    const noTransferReason = getNoTransferReason(rule);

    return (
        <YStack padding={12} borderRadius={8} backgroundColor="$white" gap={8}>
            <BodySmallRegular color="$gray1000" fontWeight="emphasized">
                Simulation Result
            </BodySmallRegular>
            <AccountTransferRoute rule={rule} sourceAccount={sourceAccount} />
            <RuleDataRow>
                <RuleDataTag text="TRANSFER" color="$gray1200" />
                <RuleDataTag
                    text={
                        transferType === 'NO_TRANSFER'
                            ? 'NONE'
                            : centsToFormattedDollars(transferAmount, false)
                    }
                    color={
                        transferType === 'NO_TRANSFER'
                            ? '$gray1000'
                            : '$green900'
                    }
                />
            </RuleDataRow>
            {noTransferReason != null && noTransferReason !== '' && (
                <MonoLabelRegular color="$red800" fontSize={12}>
                    {noTransferReason}
                </MonoLabelRegular>
            )}
            {transferType === 'NO_TRANSFER' &&
                rule.limitDetails?.remainingMonthlyCapacity != null && (
                    <MonoLabelRegular color="$gray1200" fontSize={12}>
                        Remaining monthly capacity:{' '}
                        {centsToFormattedDollars(
                            rule.limitDetails.remainingMonthlyCapacity,
                            false,
                        )}
                    </MonoLabelRegular>
                )}
            <YtdTransactionRows transactions={ytdTransactions} />
        </YStack>
    );
};

const SimulationRuleCard = ({
    rule,
    sourceAccount,
    ytdTransactions,
    validationDetail,
}: {
    rule: RulePlan;
    sourceAccount?: IncomeSplitPlanResponse['sourceAccount'];
    ytdTransactions: IncomeSplitExecutionTransaction[];
    validationDetail?: RuleValidationDetail;
}) => {
    const destinationAccount = getDestinationAccount(rule);

    if (destinationAccount == null) {
        return null;
    }

    return (
        <YStack
            padding={16}
            borderRadius={16}
            backgroundColor="$gray200"
            gap={16}
        >
            <YStack gap={4}>
                <BodyMediumRegular>
                    {destinationAccount.nickname}
                </BodyMediumRegular>
                <LabelRegular color="$gray1000">
                    {destinationAccount.accountNumberMasked}
                </LabelRegular>
            </YStack>

            {validationDetail != null && (
                <YStack
                    padding={12}
                    borderRadius={8}
                    backgroundColor="$white"
                    gap={4}
                >
                    <BodySmallRegular color="$gray1000" fontWeight="emphasized">
                        EXPERIMENTAL: Validation:{' '}
                        {validationDetail.isValid ? 'valid' : 'invalid'}
                    </BodySmallRegular>
                    {validationDetail.reason != null &&
                        validationDetail.reason !== '' && (
                            <BodySmallRegular color="$red800">
                                {validationDetail.reason}
                            </BodySmallRegular>
                        )}
                </YStack>
            )}

            <RuleConfigSection rule={rule} />
            <SimulationResultSection
                rule={rule}
                sourceAccount={sourceAccount}
                ytdTransactions={ytdTransactions}
            />
        </YStack>
    );
};

const SimulationRuleSetResults = ({
    ruleSet,
    showArrow = false,
    sourceAccount,
    ytdTransactionsByRuleId,
    validationDetails,
}: {
    ruleSet: RuleSetPlan;
    showArrow?: boolean;
    sourceAccount?: IncomeSplitPlanResponse['sourceAccount'];
    ytdTransactionsByRuleId: Record<string, IncomeSplitExecutionTransaction[]>;
    validationDetails: RuleValidationDetail[];
}) => {
    const sortedRules = [...ruleSet.rules].sort(
        (a, b) => a.priority - b.priority,
    );

    const getYtdTransactionsForRule = (rule: RulePlan) => {
        const transactionsById = new Map<
            string,
            IncomeSplitExecutionTransaction
        >();

        [
            rule.id,
            rule.originalId,
            rule.originalRule?.id,
            rule.originalRule?.originalId,
        ]
            .filter((id): id is string => id != null && id !== '')
            .forEach((ruleId) => {
                ytdTransactionsByRuleId[ruleId]?.forEach((transaction) => {
                    transactionsById.set(transaction.id, transaction);
                });
            });

        return [...transactionsById.values()].sort(
            (a, b) =>
                new Date(b.dateAdded).getTime() -
                new Date(a.dateAdded).getTime(),
        );
    };

    const getValidationForRule = (rule: RulePlan) => {
        const ruleIds = new Set(
            [
                rule.id,
                rule.originalId,
                rule.originalRule?.id,
                rule.originalRule?.originalId,
            ].filter((id): id is string => id != null && id !== ''),
        );
        return validationDetails.find((detail) => ruleIds.has(detail.ruleId));
    };

    return (
        <YStack
            gap={12}
            borderRadius={20}
            borderWidth={1}
            borderColor="$gray500"
            backgroundColor="$white"
            padding={16}
        >
            <View
                paddingVertical={8}
                paddingHorizontal={24}
                borderRadius={16}
                backgroundColor="$gray200"
                alignItems="center"
                justifyContent="center"
                height={48}
            >
                <BodySmallRegular fontStyle="italic">
                    {ruleSet.ruleSetName}
                </BodySmallRegular>
            </View>
            {!ruleSet.ruleSetReached && (
                <BodySmallRegular color="$gray1000" fontStyle="italic">
                    Rule set not reached (no remaining funds)
                </BodySmallRegular>
            )}
            <View gap={12}>
                {sortedRules.map((rule) => (
                    <SimulationRuleCard
                        key={rule.id}
                        rule={rule}
                        sourceAccount={sourceAccount}
                        ytdTransactions={getYtdTransactionsForRule(rule)}
                        validationDetail={getValidationForRule(rule)}
                    />
                ))}
            </View>
            {showArrow && (
                <YStack alignSelf="center" padding={8}>
                    <ArrowStraight />
                </YStack>
            )}
        </YStack>
    );
};

export const IncomeSplitSimulationResults = ({
    result,
    fullResponse,
}: IncomeSplitSimulationResultsProps) => {
    const sortedRuleSets = [...result.ruleSetPlans].sort(
        (a, b) => a.priority - b.priority,
    );

    const ytdTransactionsByRuleId: Record<
        string,
        IncomeSplitExecutionTransaction[]
    > = fullResponse?.ytdTransactionsByRuleId ?? {};

    return (
        <YStack gap={16}>
            <LineageAuditPanel finding={fullResponse?.lineageAudit} />
            <YStack
                gap={8}
                borderRadius={16}
                borderWidth={1}
                borderColor="$gray600"
                backgroundColor="$gray200"
                padding={16}
            >
                <BodyMediumEmphasized>Summary</BodyMediumEmphasized>
                <BodyMediumRegular>
                    Total Income:{' '}
                    {centsToFormattedDollars(result.totalIncomeAmount)}
                </BodyMediumRegular>
                <BodyMediumRegular>
                    Total Allocated:{' '}
                    {centsToFormattedDollars(result.totalAllocatedAmount)}
                </BodyMediumRegular>
                <BodyMediumRegular>
                    Remaining Funds:{' '}
                    {centsToFormattedDollars(result.finalRemainingFunds)}
                </BodyMediumRegular>
            </YStack>

            <YStack gap={12} flexGrow={1} flexShrink={1}>
                {sortedRuleSets.map((ruleSet, index) => (
                    <SimulationRuleSetResults
                        key={ruleSet.ruleSetId}
                        ruleSet={ruleSet}
                        showArrow={
                            ruleSet.ruleSetReached &&
                            index !== sortedRuleSets.length - 1
                        }
                        sourceAccount={result.sourceAccount}
                        ytdTransactionsByRuleId={ytdTransactionsByRuleId}
                        validationDetails={result.ruleValidationDetails}
                    />
                ))}
            </YStack>
        </YStack>
    );
};
